/**
 * Global Vendor List (GVL) Data Module
 * Uses bundled IAB TCF Global Vendor List
 * Filtered to only include vendors you actually use
 */

// Import bundled GVL (real IAB vendor list)
import bundledGVL from './gvl-bundled.json';

// Import vendor configuration
import { getConfiguredVendorIds } from './vendor-config.js';

// Export purposes from bundled GVL
export const purposes = bundledGVL.purposes || {};

// Export special purposes from bundled GVL
export const specialPurposes = bundledGVL.specialPurposes || {};

// Export features from bundled GVL
export const features = bundledGVL.features || {};

// Export special features from bundled GVL
export const specialFeatures = bundledGVL.specialFeatures || {};

// Export stacks from bundled GVL
export const stacks = bundledGVL.stacks || {};

// Data categories (from IAB spec)
export const dataCategories = {
  1: { id: 1, name: 'IP addresses' },
  2: { id: 2, name: 'Device characteristics' },
  3: { id: 3, name: 'Device identifiers' },
  4: { id: 4, name: 'Probabilistic identifiers' },
  5: { id: 5, name: 'Authentication-derived identifiers' },
  6: { id: 6, name: 'Browsing and interaction data' },
  7: { id: 7, name: 'User-provided data' },
  8: { id: 8, name: 'Non-precise location data' },
  9: { id: 9, name: 'Precise location data' },
  10: { id: 10, name: 'Users\' profiles' },
  11: { id: 11, name: 'Privacy choices' }
};

// All vendors from bundled GVL (for lookups)
const allVendors = bundledGVL.vendors || {};

// Filter to only active vendors (ones you actually use)
const vendors = {};
const activeVendorIds = getConfiguredVendorIds();
activeVendorIds.forEach(id => {
  if (allVendors[id]) {
    vendors[id] = allVendors[id];
  }
});

// GVL metadata
export const gvlMetadata = {
  gvlSpecificationVersion: bundledGVL.gvlSpecificationVersion || 3,
  vendorListVersion: bundledGVL.vendorListVersion || 0,
  tcfPolicyVersion: bundledGVL.tcfPolicyVersion || 5,
  lastUpdated: bundledGVL.lastUpdated || new Date().toISOString()
};

console.log(`GVL v${gvlMetadata.vendorListVersion}: Using ${Object.keys(vendors).length} configured vendors`);

/**
 * Get vendors (either full GVL or filtered subset)
 * @param {Array} vendorIds - Optional array of vendor IDs to filter
 */
export function getVendors(vendorIds = null) {
  if (vendorIds) {
    const filtered = {};
    vendorIds.forEach(id => {
      if (vendors[id]) {
        filtered[id] = vendors[id];
      }
    });
    return filtered;
  }
  return vendors;
}

/**
 * Get a specific vendor by ID
 */
export function getVendor(vendorId) {
  return vendors[vendorId] || null;
}

/**
 * Get the total number of vendors
 */
export function getVendorCount() {
  return Object.keys(vendors).length;
}

/**
 * Get all purpose IDs
 */
export function getAllPurposeIds() {
  return Object.keys(purposes).map(Number);
}

/**
 * Get all special purpose IDs
 */
export function getAllSpecialPurposeIds() {
  return Object.keys(specialPurposes).map(Number);
}

/**
 * Get all feature IDs
 */
export function getAllFeatureIds() {
  return Object.keys(features).map(Number);
}

/**
 * Get all special feature IDs
 */
export function getAllSpecialFeatureIds() {
  return Object.keys(specialFeatures).map(Number);
}

/**
 * Get all vendor IDs
 */
export function getAllVendorIds() {
  return Object.keys(vendors).map(Number);
}

/**
 * Get purposes that a vendor uses
 */
export function getVendorPurposes(vendorId) {
  const vendor = vendors[vendorId];
  if (!vendor) return [];
  return (vendor.purposes || []).map(pid => purposes[pid]).filter(Boolean);
}

/**
 * Get the max vendor ID in the list
 */
export function getMaxVendorId() {
  const ids = getAllVendorIds();
  return ids.length > 0 ? Math.max(...ids) : 0;
}

/**
 * Check if a vendor is deleted
 */
export function isVendorDeleted(vendorId) {
  const vendor = vendors[vendorId];
  return vendor && vendor.deletedDate ? true : false;
}

/**
 * Get all active (non-deleted) vendor IDs
 */
export function getActiveVendorIds() {
  return Object.keys(vendors)
    .map(Number)
    .filter(id => !isVendorDeleted(id));
}

/**
 * Get deleted vendor IDs
 */
export function getDeletedVendorIds() {
  return Object.keys(vendors)
    .map(Number)
    .filter(id => isVendorDeleted(id));
}

/**
 * Initialize GVL - call this on startup (now synchronous since bundled)
 */
export async function initGVL() {
  // GVL is already loaded from bundle
  return {
    vendors,
    purposes,
    specialPurposes,
    features,
    specialFeatures,
    gvlMetadata
  };
}

/**
 * Get the full GVL data (for getVendorList API command)
 */
export function getFullGVL() {
  return {
    gvlSpecificationVersion: gvlMetadata.gvlSpecificationVersion,
    vendorListVersion: gvlMetadata.vendorListVersion,
    tcfPolicyVersion: gvlMetadata.tcfPolicyVersion,
    lastUpdated: gvlMetadata.lastUpdated,
    purposes,
    specialPurposes,
    features,
    specialFeatures,
    stacks,
    vendors
  };
}

/**
 * Format seconds to human-readable duration
 */
export function formatDuration(seconds) {
  if (!seconds) return 'Not specified';
  const days = Math.round(seconds / 86400);
  if (days >= 365) {
    const years = Math.round(days / 365);
    return `${years} year${years > 1 ? 's' : ''}`;
  } else if (days >= 30) {
    const months = Math.round(days / 30);
    return `${months} month${months > 1 ? 's' : ''}`;
  }
  return `${days} day${days > 1 ? 's' : ''}`;
}

/**
 * Get vendor storage info formatted for display
 */
export function getVendorStorageInfo(vendorId) {
  const vendor = vendors[vendorId];
  if (!vendor) return null;
  
  const maxAge = formatDuration(vendor.cookieMaxAgeSeconds);
  const refreshes = vendor.cookieRefresh ? 'Yes, may refresh on interaction' : 'No';
  const usesCookies = vendor.usesCookies ? 'Yes' : 'No';
  const usesNonCookie = vendor.usesNonCookieAccess ? 'Yes' : 'No';
  
  return {
    maxStorageDuration: maxAge,
    storageRefreshes: refreshes,
    usesCookies,
    usesNonCookieAccess: usesNonCookie
  };
}

/**
 * Get vendor data retention info
 */
export function getVendorRetentionInfo(vendorId) {
  const vendor = vendors[vendorId];
  if (!vendor?.dataRetention) return null;
  
  const retention = vendor.dataRetention;
  const result = {
    standardRetention: formatDuration(retention.stdRetention * 86400), // days to seconds
    purposeSpecific: {}
  };
  
  if (retention.purposes) {
    Object.entries(retention.purposes).forEach(([purposeId, days]) => {
      const purpose = purposes[purposeId];
      result.purposeSpecific[purposeId] = {
        name: purpose?.name || `Purpose ${purposeId}`,
        retention: formatDuration(days * 86400)
      };
    });
  }
  
  return result;
}

/**
 * Get vendor data categories
 */
export function getVendorDataCategories(vendorId) {
  const vendor = vendors[vendorId];
  if (!vendor?.dataDeclaration) return [];
  
  return vendor.dataDeclaration.map(catId => dataCategories[catId]).filter(Boolean);
}

/**
 * Get vendors that use a specific purpose (for vendor count per purpose)
 */
export function getVendorCountForPurpose(purposeId) {
  return Object.values(vendors).filter(v => 
    v.purposes?.includes(purposeId) || v.legIntPurposes?.includes(purposeId)
  ).length;
}

/**
 * Get vendors that use legitimate interest for a purpose
 */
export function getVendorsWithLegIntForPurpose(purposeId) {
  return Object.values(vendors).filter(v => v.legIntPurposes?.includes(purposeId));
}
